import { ChevronDownIcon, ChevronUpIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { Checkbox } from 'stories/components/Checkbox/Checkbox'
import { confirm, generateUniqeId, prompt } from 'utils'

interface IOrderValuationConfig {
  title: string
  visible: boolean
}

type TableColumn = {
  key: string
  title: string
  visible: boolean
}

const defaultOptions = [
  'AEI Consultants',
  'Appraisal Nation',
  'BBG Real Estate Services',
  'Bowery Valuations',
  'CBRE Commercial Real Estate Services',
  'CFSI Management',
  'Clear Capital',
  'Colliers',
  'Cushman & Wakefield',
  'Elite Valuations',
  'Hillman Consulting',
  'IRR Integra Realty Resources',
  'KOW Building Consultants',
  'Land Gorilla',
  'Nationwide Appraisal Network (NAN)',
  'Nationwide AMC (NAAN)',
  'Newmark Knight Frank',
  'Partners ESI',
  'Trinity',
  'Valuation Services AMC (VSAMC)',
  'Arivs',
  'Vision VMC',
  'AMC Direct',
  'Inspectify',
  'Class Valuation',
]

const defaultTableColumns: TableColumn[] = [
  { key: 'orderDueDate', title: 'Order Date / Due Date', visible: true },
  { key: 'id', title: 'Order ID', visible: true },
  { key: 'orderTypeForm', title: 'Order Type / Appraisal Form', visible: true },
  { key: 'vendor', title: 'Vendor', visible: true },
  { key: 'status', title: 'Status', visible: true },
  { key: 'createdBy', title: 'By/Date', visible: true },
  { key: 'inspectionScheduled', title: 'Inspection Scheduled', visible: true },
  { key: 'inspectionCompleted', title: 'Inspection Completed', visible: true },
  { key: 'appraisalCost', title: 'Appraisal Cost', visible: true },
  { key: 'paymentStatus', title: 'Payment Status', visible: true },
  { key: 'loanNumber', title: 'Loan Number', visible: false },
  { key: 'borrower', title: 'Borrower', visible: false },
  { key: 'propertyAddress', title: 'Property Address', visible: false },
]

export const OrderValuation = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, IOrderValuationConfig>>({})
  const [tableColumns, setTableColumns] = useState<TableColumn[]>(defaultTableColumns)
  const [isChanged, setChanged] = useState(false)

  useEffect(() => {
    setLoading(true)
    Promise.all([getAdminConfig('orderValuation'), getAdminConfig('orderValuationTableColumns')])
      .then(([vendors, columns]) => {
        setData(vendors)
        if (Array.isArray(columns) && columns.length) setTableColumns(columns)
      })
      .finally(() => setLoading(false))
  }, [])

  const onSubmit = async () => {
    setLoading(true)

    await Promise.all([
      setAdminConfig('orderValuation', data),
      setAdminConfig('orderValuationTableColumns', tableColumns),
    ])
      .then(() => {
        setChanged(false)
        toast(`Order Valuation Configuration has been saved.`, { type: 'info' })
      })
      .finally(() => setLoading(false))
  }

  const onChange = (key: string, value: any, map: string) => {
    let temp = cloneDeep(data) as any
    if (temp[key] === undefined) temp[key] = {}
    temp[key][map] = value
    setData(temp)
    setChanged(true)
  }

  const onAdd = async () => {
    const title = await prompt('Please enter the new title')
    if (!title) return
    const newData = cloneDeep(data)
    const newKey = generateUniqeId(title, Object.keys(data))
    if (!newKey) return
    newData[newKey] = {
      title,
      visible: true,
    }
    setData(newData)
    setChanged(true)
  }

  const onRemove = async (key: string) => {
    const result = await confirm('Are you sure you want to remove this item?')
    if (!result) return
    const newData = cloneDeep(data)
    delete newData[key]
    setData(newData)
    setChanged(true)
  }

  const onColumnChange = (index: number, field: 'title' | 'visible', value: any) => {
    const next = cloneDeep(tableColumns)
    next[index][field] = value
    setTableColumns(next)
    setChanged(true)
  }

  const moveColumn = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= tableColumns.length) return
    const next = cloneDeep(tableColumns)
    const [row] = next.splice(index, 1)
    next.splice(target, 0, row)
    setTableColumns(next)
    setChanged(true)
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSubmit} />
      <div className="mt-4">
        <div className="font-semibold mb-2">Vendors</div>
        <table className="text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Title', 'In Use', 'Action'].map((v) => (
                <th scope="col" className="p-3 border" key={`header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((key, index) => {
              const { title, visible } = data[key]
              const isCustom = !defaultOptions.includes(key)
              return (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="px-3 border">
                    <PlainInput
                      value={title}
                      content={title}
                      onChange={(value: string) => onChange(key, value, 'title')}
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <Checkbox
                      color="gray"
                      id={key}
                      title={''}
                      key={key}
                      value={visible}
                      onChange={(value) => onChange(key, value, 'visible')}
                      className="w-[none]"
                    />
                  </td>
                  <td className="px-3 border">
                    {isCustom && (
                      <div className="flex">
                        <span className="btn-icon" onClick={() => onRemove(key)}>
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <Button link onClick={onAdd}>
          + Add
        </Button>
      </div>

      <div className="mt-8">
        <div className="font-semibold mb-2">Orders Table Columns</div>
        <table className="text-sm w-full max-w-3xl">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Column', 'Visible', 'Order'].map((v) => (
                <th scope="col" className="p-3 border" key={`col-header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableColumns.map((col, index) => (
              <tr key={col.key}>
                <td className="p-2 border">{index + 1}</td>
                <td className="px-3 border">
                  <PlainInput
                    value={col.title}
                    content={col.title}
                    onChange={(value: string) => onColumnChange(index, 'title', value)}
                  />
                </td>
                <td className="px-3 border text-center">
                  <Checkbox
                    color="gray"
                    id={`col-${col.key}`}
                    title=""
                    value={col.visible}
                    onChange={(value) => onColumnChange(index, 'visible', value)}
                    className="w-[none]"
                  />
                </td>
                <td className="px-3 border">
                  <div className="flex gap-1">
                    <button type="button" className="btn-icon" onClick={() => moveColumn(index, -1)}>
                      <ChevronUpIcon className="w-4 h-4" />
                    </button>
                    <button type="button" className="btn-icon" onClick={() => moveColumn(index, 1)}>
                      <ChevronDownIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
